/**
 * PublicBooking — Premium booking page (Feb 2026).
 * Route: /book/:slug
 *
 * Design language matches /share/:token — pine-gradient hero, elegant
 * slot cards grouped by day, minimal 4-field form, celebratory
 * confirmation screen with Google Calendar / Directions / Call actions.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import {
  Phone, MessageCircle, MapPin, Calendar, Clock, ChevronRight, Loader2,
  ShieldCheck, Sparkles, Stethoscope, User as UserIcon, AlertTriangle,
  ArrowLeft, CheckCircle2, Download, Star, User, Users,
} from "lucide-react";
import SEO from "@/components/SEO";
import { apiError } from "@/lib/apiError";

const BASE = process.env.REACT_APP_BACKEND_URL;
const api = axios.create({ baseURL: `${BASE}/api` });

const DOW = ["monday","tuesday","wednesday","thursday","friday","saturday","sunday"];
// Time / date formatters that render in the CLINIC's timezone rather than the
// visitor's browser timezone. When the caller passes the clinic tz (from
// `data.clinic.timezone` or the /available-slots response), a patient sitting
// in London still sees "5:00 pm Monday" for a Delhi clinic — the way the
// clinic actually operates. Falls back to `undefined` (browser locale/tz) when
// tz isn't known, which preserves existing behaviour for offline safety.
const dayISO = (d) => d.toISOString().slice(0, 10);
import { fmtTime24 } from "@/lib/time";
// Standardized to 24-hour HH:mm — no locale-dependent AM/PM.
const fmtTime = (iso, _tz) => fmtTime24(iso);
const fmtLongDate = (iso, tz) => new Date(iso).toLocaleDateString("en-IN",
  { weekday: "long", day: "numeric", month: "long", year: "numeric",
    ...(tz ? { timeZone: tz } : {}) });
const greeting = () => {
  const h = new Date().getHours();
  return h < 5 ? "Good evening" : h < 12 ? "Good morning" : h < 17 ? "Good afternoon" : "Good evening";
};

export default function PublicBooking() {
  const { slug } = useParams();
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  const [step, setStep] = useState("pick");  // pick → form → done
  const [selected, setSelected] = useState(null); // slot iso
  const [booked, setBooked] = useState(null); // booking result
  const [adminPreview, setAdminPreview] = useState(false);
  // Only surface the "Admin preview" toggle if the visitor is signed in to
  // Viyora (any role). We just detect the auth cookie via a lightweight
  // /auth/me probe — the actual preview privilege is verified on the server.
  const [canPreview, setCanPreview] = useState(false);
  // Currently-selected doctor for the booking. Kept at the page level so
  // HeroCard chips, SlotPicker (availability query), and BookingForm all
  // stay in sync — clicking a doctor immediately refreshes the slot picker
  // AND is carried forward as `doctor_id` on the /book POST. Initialised
  // from `?doctor=<id>` (personal booking links). Otherwise stays empty
  // so the patient has to actively pick — no ghost preselection.
  const [selectedDoctorId, setSelectedDoctorId] = useState(() => {
    if (typeof window === "undefined") return "";
    return new URLSearchParams(window.location.search).get("doctor") || "";
  });
  // When true, the user picked via the "First Available" card. We use this
  // to (a) visually mark the FA card as selected instead of the resolved
  // doctor's card, and (b) keep the selection tracking the earliest
  // available doctor as new bookings come in and the ordering shifts.
  const [firstAvailablePicked, setFirstAvailablePicked] = useState(false);

  // Wrapper so specific-doctor clicks always clear the "First Available"
  // flag. Prevents the FA card from staying visually selected after the
  // patient picks a specific doctor.
  const pickSpecificDoctor = useCallback((doctorId) => {
    setFirstAvailablePicked(false);
    setSelectedDoctorId(doctorId);
  }, []);
  const pickFirstAvailable = useCallback((doctorId) => {
    setFirstAvailablePicked(true);
    setSelectedDoctorId(doctorId);
  }, []);

  useEffect(() => {
    api.get("/auth/me", { withCredentials: true })
      .then((r) => setCanPreview(!!r.data))
      .catch(() => setCanPreview(false));
  }, []);

  useEffect(() => {
    const params = adminPreview ? { admin_preview: 1 } : {};
    api.get(`/public/clinics/${slug}`, { params, withCredentials: true })
      .then((r) => setData(r.data))
      .catch((e) => setErr(apiError(e, "Clinic not found.")));
  }, [slug, adminPreview]);

  // If the visitor came in via ?doctor=<id> but that id is no longer on
  // the roster (e.g. doctor deactivated between the link being copied and
  // clicked), clear the stale selection. Do NOT auto-pick another doctor
  // — the patient must actively choose so no ghost "SELECTED" state
  // appears on load.
  useEffect(() => {
    if (!data?.doctors?.length) return;
    setSelectedDoctorId((current) => {
      if (current && data.doctors.some((d) => d.id === current)) return current;
      return "";
    });
  }, [data?.doctors]);

  if (err) return <ErrorState message={err} />;
  if (!data) return <LoadingState />;

  const { clinic, procedures, doctors } = data;
  const isAdminPreview = !!data.admin_preview;
  const hiddenIds = new Set(data.hidden_procedure_ids || []);
  const hiddenLabel = data.pricing_hidden_label || "Fee will be discussed during consultation";

  if (clinic.accepts_online_bookings === false) {
    return <ErrorState message="This clinic is not accepting online bookings right now. Please call them directly." clinic={clinic} />;
  }

  return (
    <div className="min-h-screen bg-[#F6F5F1] text-ink pb-24" data-testid="public-booking-page">
      <SEO title={`Book · ${clinic.name || "Clinic"}`} path={`/book/${slug}`} />
      <BgOrnaments />
      {canPreview && (
        <div className="fixed top-4 right-4 z-50">
          <button
            onClick={() => setAdminPreview((v) => !v)}
            data-testid="btn-admin-preview"
            className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-medium shadow-md transition-colors ${
              isAdminPreview
                ? "bg-[#D06A42] text-white hover:bg-[#E17550]"
                : "bg-white/90 text-ink border border-[#E7E1D3] hover:bg-white"
            }`}
            title="Toggle admin preview — see prices as they would appear if visibility were fully on">
            <Sparkles className="w-3.5 h-3.5" />
            {isAdminPreview ? "Admin preview on" : "Admin preview"}
          </button>
        </div>
      )}
      <div className="relative max-w-4xl mx-auto px-4 sm:px-6 pt-6 sm:pt-10 space-y-6">
        <HeroCard clinic={clinic} />

        {isAdminPreview && (
          <div className="rounded-2xl bg-[#FFF4E5] border border-[#F2D2BC] px-4 py-3 text-xs text-[#8A4B00] flex items-center gap-2"
               data-testid="admin-preview-banner">
            <Sparkles className="w-4 h-4 shrink-0" />
            <span>
              <strong>Admin preview</strong> — you're seeing the page as an admin. Prices tagged
              <em className="mx-1">Hidden</em> would be replaced with &ldquo;{hiddenLabel}&rdquo; for the public.
            </span>
          </div>
        )}

        {step === "pick" && (
          <>
            <DoctorPicker
              slug={slug}
              clinic={clinic}
              doctors={doctors}
              selectedDoctorId={selectedDoctorId}
              firstAvailablePicked={firstAvailablePicked}
              onSelectDoctor={pickSpecificDoctor}
              onSelectFirstAvailable={pickFirstAvailable}
            />
            <SlotPicker
              slug={slug} clinic={clinic}
              selected={selected}
              selectedDoctorId={selectedDoctorId}
              onPick={(iso) => { setSelected(iso); setStep("form"); }}
            />
          </>
        )}

        {step === "form" && (
          <BookingForm
            slug={slug} clinic={clinic} procedures={procedures} doctors={doctors}
            selectedISO={selected}
            selectedDoctorId={selectedDoctorId}
            onDoctorChange={setSelectedDoctorId}
            hiddenIds={hiddenIds}
            hiddenLabel={hiddenLabel}
            isAdminPreview={isAdminPreview}
            onBack={() => setStep("pick")}
            onBooked={(res) => { setBooked(res); setStep("done"); }}
          />
        )}

        {step === "done" && booked && (
          <ConfirmationCard clinic={clinic} selectedISO={selected} result={booked} />
        )}

        <TrustFooter clinic={clinic} />
      </div>
    </div>
  );
}

/* -------------------------- shared bits -------------------------- */
function BgOrnaments() {
  return (
    <div className="pointer-events-none fixed inset-0 overflow-hidden">
      <div className="absolute -top-40 -left-40 w-[520px] h-[520px] rounded-full bg-gradient-to-br from-[#1A362B]/8 to-transparent blur-3xl" />
      <div className="absolute -top-16 right-0 w-[440px] h-[440px] rounded-full bg-gradient-to-br from-[#D06A42]/10 to-transparent blur-3xl" />
    </div>
  );
}

/* -------------------------- HERO -------------------------- */
function HeroCard({ clinic }) {
  const phone = clinic?.phone || "";
  const digits = phone.replace(/[^\d+]/g, "");
  const waNum = digits.replace(/^\+/, "");
  const mapUrl = clinic?.google_maps_url
    || (clinic?.address ? `https://maps.google.com/?q=${encodeURIComponent(clinic.address)}` : null);
  const today = DOW[new Date().getDay() === 0 ? 6 : new Date().getDay() - 1];
  const todayHours = clinic?.working_hours?.[today];
  const isOpenToday = todayHours && !todayHours.closed && todayHours.open && todayHours.close;

  return (
    <section className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-[#1A362B] via-[#264A3B] to-[#12291F] text-linen shadow-[0_30px_80px_-30px_rgba(26,54,43,0.55)]"
             data-testid="booking-hero">
      {clinic.cover_image_url && (
        <div className="absolute inset-0 opacity-20"
             style={{ backgroundImage: `url(${clinic.cover_image_url})`,
                      backgroundSize: "cover", backgroundPosition: "center" }} />
      )}
      <div className="absolute inset-0 opacity-[0.06] mix-blend-overlay"
           style={{ backgroundImage: "radial-gradient(rgba(255,255,255,.35) 1px, transparent 1px)", backgroundSize: "3px 3px" }} />
      <div className="absolute -top-24 -right-24 w-72 h-72 rounded-full bg-white/5" />

      <div className="relative p-6 sm:p-10">
        <div className="flex items-start justify-between gap-4">
          <div className="flex items-center gap-4 min-w-0">
            {clinic.logo_url ? (
              <img src={clinic.logo_url} alt={clinic.name}
                   className="w-16 h-16 rounded-2xl object-cover bg-white/90 shadow-lg shadow-black/20" />
            ) : (
              <div className="w-16 h-16 rounded-2xl grid place-items-center bg-white text-[#1A362B] font-display text-lg font-semibold shadow-lg shadow-black/20">
                {(clinic.name || "V").trim()[0]?.toUpperCase()}
              </div>
            )}
            <div className="min-w-0">
              <div className="text-[11px] uppercase tracking-[0.22em] text-linen/60">Modern dental care</div>
              <div className="font-display text-xl sm:text-2xl truncate">{clinic.name}</div>
              {clinic.specialities?.length > 0 && (
                <div className="text-xs text-linen/60 mt-1">{clinic.specialities.slice(0, 3).join(" · ")}</div>
              )}
              {clinic.google && (
                <a
                  href={clinic.google.maps_url || "#"}
                  target={clinic.google.maps_url ? "_blank" : undefined}
                  rel="noopener noreferrer"
                  onClick={(e) => { if (!clinic.google.maps_url) e.preventDefault(); }}
                  className={[
                    "mt-2 inline-flex items-center gap-2 text-xs bg-white/10",
                    "px-2.5 py-1 rounded-full transition-colors",
                    clinic.google.maps_url ? "hover:bg-white/15 cursor-pointer" : "cursor-default",
                  ].join(" ")}
                  data-testid="hero-google-rating"
                  aria-label={`Google rating ${clinic.google.rating} from ${clinic.google.review_count} reviews`}
                >
                  <Star className="w-3 h-3 text-[#F5B301] fill-[#F5B301]" />
                  <span className="font-semibold text-linen">{clinic.google.rating.toFixed(1)}</span>
                  <span className="text-linen/70">·</span>
                  <span className="text-linen/70">{clinic.google.review_count.toLocaleString("en-IN")} Google reviews</span>
                </a>
              )}
            </div>
          </div>
          <div className="hidden sm:flex flex-col items-end text-xs text-linen/60 shrink-0">
            {isOpenToday && (
              <span className="inline-flex items-center gap-1 bg-[#2D6A4F]/25 text-[#B8D6C2] px-3 py-1 rounded-full">
                <span className="w-1.5 h-1.5 rounded-full bg-[#8EE4AA] animate-pulse" /> Open today · {todayHours.open}–{todayHours.close}
              </span>
            )}
          </div>
        </div>

        <div className="mt-8 sm:mt-10">
          <div className="text-linen/60 text-sm">{greeting()},</div>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[1.05]">
            Book your visit
          </h1>
          {clinic.about && (
            <p className="mt-3 max-w-lg text-linen/75 text-sm sm:text-base">{clinic.about}</p>
          )}
        </div>

        <div className="mt-8 flex flex-wrap gap-2 sm:gap-3">
          {digits && (
            <a href={`tel:${digits}`} data-testid="btn-call-clinic" className="btn-hero btn-hero-primary">
              <Phone className="w-4 h-4" /> Call clinic
            </a>
          )}
          {waNum && (
            <a href={`https://wa.me/${waNum}`} target="_blank" rel="noopener noreferrer"
               data-testid="btn-whatsapp" className="btn-hero btn-hero-ghost">
              <MessageCircle className="w-4 h-4" /> WhatsApp
            </a>
          )}
          {mapUrl && (
            <a href={mapUrl} target="_blank" rel="noopener noreferrer" data-testid="btn-directions"
               className="btn-hero btn-hero-ghost"><MapPin className="w-4 h-4" /> Directions</a>
          )}
        </div>

        {/* Doctor selection now lives in the dedicated <DoctorPicker/>
            section below this hero — see PublicBookingInner. */}
      </div>

      <style>{`
        .btn-hero { display: inline-flex; align-items: center; gap: .5rem; padding: .65rem 1.1rem;
                    border-radius: 999px; font-size: .875rem; font-weight: 500;
                    transition: transform .15s ease, background .2s ease; white-space: nowrap; }
        .btn-hero:active { transform: scale(.97); }
        .btn-hero-primary { background: #F5F3ED; color: #1A362B; }
        .btn-hero-primary:hover { background: #ffffff; }
        .btn-hero-ghost { background: rgba(255,255,255,.08); color: #F5F3ED; border: 1px solid rgba(255,255,255,.14); }
        .btn-hero-ghost:hover { background: rgba(255,255,255,.14); }
        @keyframes fadeInDown {
          from { opacity: 0; transform: translateY(-6px) scale(.95); }
          to   { opacity: 1; transform: translateY(0)    scale(1); }
        }
        @keyframes popIn {
          from { opacity: 0; transform: scale(.6) rotate(-8deg); }
          to   { opacity: 1; transform: scale(1)  rotate(0);      }
        }
      `}</style>
    </section>
  );
}

/* -------------------------- DOCTOR PICKER (premium cards) -------------- */
/**
 * Premium doctor selection grid. Renders every bookable doctor as a rich
 * card (photo, qualification, specialty, years, rating, next-available,
 * consultation fee) plus a special "First Available" card that auto-picks
 * the doctor with the earliest open slot.
 *
 * Data contract:
 *  - Doctor cards read straight from the /public/clinics/{slug} response
 *    (`doctors: []`).  Fields that are unset on the DB (qualification,
 *    years_experience, rating…) are simply omitted — never placeholdered.
 *  - "Next available" is computed by a lightweight parallel fetch of
 *    /public/clinics/{slug}/available-slots for the next 7 days per doctor.
 *    Refreshes every 60s so newly-booked slots move the pill in near-real
 *    time.  This does NOT add or change any booking logic — it's a pure
 *    read against the endpoint the SlotPicker already uses.
 */
function DoctorPicker({ slug, clinic, doctors, selectedDoctorId,
                        firstAvailablePicked, onSelectDoctor,
                        onSelectFirstAvailable }) {
  const tz = clinic?.timezone;
  // Per-doctor availability state:
  //   iso   — earliest available slot ISO across the lookahead window
  //   date  — the day the earliest slot lives on
  //   todayStatus — { closed, reason } response for TODAY specifically, so
  //     the card can say "Clinic closed", "On leave", "Fully booked today",
  //     etc. instead of a generic empty state.
  const [nextSlotByDoctor, setNextSlotByDoctor] = useState({});
  const [loadingSlots, setLoadingSlots] = useState(true);

  // Look ahead one full week — enough to always find *something* even for
  // part-time doctors, without blowing up on API calls.
  const LOOKAHEAD_DAYS = 7;

  const lookupDates = useMemo(() => {
    const arr = [];
    for (let i = 0; i < LOOKAHEAD_DAYS; i++) {
      const d = new Date(); d.setDate(d.getDate() + i);
      arr.push(dayISO(d));
    }
    return arr;
  }, []);

  // Fire one /available-slots call per (doctor × day) — happens in parallel
  // and is capped at doctors.length * 7 requests (max ~35 for a 5-doctor
  // clinic). Each doctor's earliest positive slot wins; we ALSO memorise
  // today's response so we can render granular "on leave / clinic closed /
  // fully booked" copy on the card instead of a bland empty state.
  const loadNextSlots = useCallback(async () => {
    if (!doctors?.length) { setLoadingSlots(false); return; }
    setLoadingSlots(true);
    const todayISO = lookupDates[0];
    const per = await Promise.all(doctors.map(async (doc) => {
      let earliest = null;
      let todayStatus = null;
      for (const d of lookupDates) {
        try {
          const { data } = await api.get(
            `/public/clinics/${slug}/available-slots`,
            { params: { date: d, doctor_id: doc.id } },
          );
          if (d === todayISO) {
            todayStatus = { closed: !!data?.closed, reason: data?.reason || null,
                            hasSlots: !!(data?.slots?.length) };
          }
          if (!earliest && data?.slots?.length) {
            earliest = { iso: data.slots[0].start_iso, date: d };
            if (todayStatus) break; // we have both signals — stop early
          }
        } catch {/* skip */}
      }
      return [doc.id, { ...(earliest || {}), todayStatus }];
    }));
    setNextSlotByDoctor(Object.fromEntries(per));
    setLoadingSlots(false);
  }, [slug, doctors, lookupDates]);

  useEffect(() => {
    loadNextSlots();
    const t = setInterval(loadNextSlots, 60000);
    return () => clearInterval(t);
  }, [loadNextSlots]);

  // "First available" auto-selects the doctor whose next slot lands soonest.
  const firstAvailableDoctorId = useMemo(() => {
    let best = null;
    for (const doc of doctors || []) {
      const ns = nextSlotByDoctor[doc.id];
      if (!ns) continue;
      if (!best || new Date(ns.iso) < new Date(best.iso)) {
        best = { doctorId: doc.id, iso: ns.iso };
      }
    }
    return best?.doctorId || null;
  }, [doctors, nextSlotByDoctor]);

  // If the user picked "First Available" and the earliest-slot doctor
  // changes (new bookings shift the ordering), keep the underlying
  // selectedDoctorId synced so the SlotPicker always shows the latest
  // "first" doctor's real availability. Silent no-op when user picked a
  // specific doctor.
  useEffect(() => {
    if (firstAvailablePicked && firstAvailableDoctorId
        && firstAvailableDoctorId !== selectedDoctorId) {
      onSelectFirstAvailable(firstAvailableDoctorId);
    }
  }, [firstAvailablePicked, firstAvailableDoctorId, selectedDoctorId, onSelectFirstAvailable]);

  if (!doctors?.length) return null;

  return (
    <section
      className="rounded-3xl bg-white/70 backdrop-blur-md border border-[#E7E1D3] p-5 sm:p-8 shadow-sm"
      data-testid="doctor-picker"
    >
      <div className="flex items-start justify-between gap-3 mb-5 flex-wrap">
        <div className="flex items-start gap-3">
          <div className="w-9 h-9 rounded-full grid place-items-center bg-[#FFF0E5] text-[#D06A42] shrink-0">
            <User className="w-4 h-4" />
          </div>
          <div>
            <div className="font-display text-xl sm:text-2xl text-ink leading-tight">
              1. Choose your dentist
            </div>
            <div className="text-xs text-ink-mid mt-1">
              {doctors.length === 1
                ? "Your dentist for this visit"
                : "Select the doctor you'd like to book with"}
            </div>
          </div>
        </div>
      </div>

      {/*
        Responsive layout:
          - Mobile  (< sm): horizontal scroll-snap carousel
          - Tablet  (sm+):  2 columns
          - Desktop (lg+):  3 columns
        Cards use a fixed min-width on mobile so the carousel feels natural.
      */}
      <div
        role="radiogroup"
        aria-label="Select doctor"
        className={[
          "gap-4",
          "flex overflow-x-auto snap-x snap-mandatory scroll-pl-4 pb-2 -mx-1 px-1",
          "sm:grid sm:grid-cols-2 sm:overflow-visible sm:mx-0 sm:px-0",
          "lg:grid-cols-3",
        ].join(" ")}
        data-testid="booking-doctors"
      >
        {doctors.map((doc) => (
          <DoctorCard
            key={doc.id}
            doctor={doc}
            nextSlot={nextSlotByDoctor[doc.id]}
            nextSlotLoading={loadingSlots && !(doc.id in nextSlotByDoctor)}
            selected={!firstAvailablePicked && doc.id === selectedDoctorId}
            tz={tz}
            onSelect={() => onSelectDoctor(doc.id)}
          />
        ))}
        {doctors.length > 1 && (
          <FirstAvailableCard
            targetDoctor={doctors.find((d) => d.id === firstAvailableDoctorId)}
            nextSlot={firstAvailableDoctorId ? nextSlotByDoctor[firstAvailableDoctorId] : null}
            selected={firstAvailablePicked}
            tz={tz}
            onSelect={() => firstAvailableDoctorId && onSelectFirstAvailable(firstAvailableDoctorId)}
          />
        )}
      </div>
    </section>
  );
}

/**
 * A single premium doctor card. Layout mirrors the reference design:
 * photo top-left, name / specialty / qualification top-right, rating +
 * availability pill in the middle, consultation-fee row and CTA button
 * at the bottom. All non-mandatory fields are omitted when unset — the
 * card never fabricates data.
 */
function DoctorCard({ doctor, nextSlot, nextSlotLoading, selected, onSelect, tz }) {
  const d = doctor;
  const hasNextSlot = !!nextSlot?.iso;
  const isToday = hasNextSlot &&
    new Date(nextSlot.iso).toDateString() === new Date().toDateString();
  // `todayStatus` comes from the engine's response for today. Codes we care
  // about: doctor_on_leave, clinic_holiday, clinic_closed_this_day,
  // date_blocked, doctor_unavailable, doctor_not_bookable,
  // online_bookings_disabled. `closed=false && !hasSlots` means the day is
  // technically open but every chair is taken — "Fully booked today".
  const ts = nextSlot?.todayStatus || null;
  const feeVisible = !d.consultation_fee_hidden;
  const feeNum = d.consultation_fee;
  const feeLabel =
    (d.consultation_fee_charge_scope === "free" || (feeVisible && (feeNum === 0 || feeNum == null)))
      ? "Free"
      : (feeVisible && typeof feeNum === "number")
        ? `₹${Number(feeNum).toLocaleString("en-IN")}`
        : null;

  const cardBase = [
    "relative snap-start shrink-0 w-[85vw] max-w-[340px] sm:w-auto sm:max-w-none",
    "rounded-2xl border p-5 flex flex-col text-left transition-all duration-200 ease-out",
    "bg-white",
    selected
      ? "border-[#D06A42] shadow-[0_20px_50px_-20px_rgba(208,106,66,0.55)] sm:scale-[1.015] ring-1 ring-[#D06A42]/20"
      : "border-[#EFEADC] hover:border-[#D06A42]/40 hover:shadow-[0_12px_36px_-24px_rgba(0,0,0,0.25)]",
  ].join(" ");

  return (
    <button
      type="button"
      role="radio"
      aria-checked={selected}
      onClick={onSelect}
      data-testid={`booking-doctor-${d.id}`}
      data-selected={selected ? "true" : "false"}
      className={cardBase}
    >
      {/* Selected "Recommended" pill top-left, animates in when selected. */}
      {selected && (
        <span
          className="absolute -top-2.5 left-4 inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-[#D06A42] text-white text-[10px] font-semibold uppercase tracking-widest shadow-md animate-[fadeInDown_.25s_ease-out]"
          data-testid={`doctor-selected-badge-${d.id}`}
        >
          <Star className="w-3 h-3" /> Selected
        </span>
      )}

      {/* Header: photo + name/specialty/qualification */}
      <div className="flex items-start gap-4 mb-4">
        <div className="w-14 h-14 shrink-0 rounded-full overflow-hidden ring-2 ring-[#F5EFDF] grid place-items-center">
          {d.picture ? (
            <img src={d.picture} alt={d.name} className="w-full h-full object-cover" />
          ) : (
            <div className={`w-full h-full grid place-items-center text-sm font-semibold ${_avatarTint(d.id)}`}>
              {_initials(d.name)}
            </div>
          )}
        </div>
        <div className="min-w-0 flex-1">
          <div className="font-medium text-ink truncate">{d.name || "Doctor"}</div>
          {d.specialty && (
            <div className="text-sm text-[#D06A42] font-medium truncate mt-0.5">
              {d.specialty}
            </div>
          )}
          {(d.qualification || typeof d.years_experience === "number") && (
            <div className="text-xs text-ink-mid mt-1 flex items-center gap-1.5 flex-wrap">
              {d.qualification && <span>{d.qualification}</span>}
              {d.qualification && typeof d.years_experience === "number" && (
                <span className="text-ink-soft">•</span>
              )}
              {typeof d.years_experience === "number" && (
                <span>{d.years_experience} Years Exp.</span>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Rating (optional) — DEPRECATED at the doctor level.
          Google reviews are a clinic-level concept (see HeroCard) and are
          intentionally NOT shown on individual doctor cards anymore. */}

      {/* Availability badge — reflects the engine's real state:
            • Available Today     — today has open slots
            • Fully booked today  — today's shift is open but capacity=0
            • On leave            — reason=doctor_on_leave
            • Clinic closed today — clinic_closed_this_day / clinic_holiday
            • Online booking off  — doctor_not_bookable / disabled
            • Next available <X>  — today unavailable but future slot exists
            • No open slots       — nothing in the whole lookahead window
       */}
      <div className="mb-4" data-testid={`doctor-availability-${d.id}`}>
        {nextSlotLoading ? (
          <div className="inline-flex items-center gap-1.5 rounded-lg bg-[#F5F0E3] px-3 py-2 text-xs text-ink-mid">
            <Loader2 className="w-3 h-3 animate-spin" /> Checking availability…
          </div>
        ) : hasNextSlot && isToday ? (
          <div className="inline-flex items-center gap-1.5 rounded-lg bg-[#E8F0EB] px-3 py-2 text-xs font-medium text-[#2D6A4F]">
            <span className="w-1.5 h-1.5 rounded-full bg-[#2D8659] animate-pulse" />
            Available Today
          </div>
        ) : ts?.reason === "doctor_on_leave" ? (
          hasNextSlot ? (
            <div className="inline-flex items-center gap-1.5 rounded-lg bg-[#FDF0E5] px-3 py-2 text-xs font-medium text-[#B0521F]">
              <Calendar className="w-3 h-3" />
              On leave · Next available {new Date(nextSlot.iso).toLocaleDateString("en-IN",
                { weekday: "short", day: "numeric", month: "short",
                  ...(tz ? { timeZone: tz } : {}) })} {fmtTime(nextSlot.iso, tz)}
            </div>
          ) : (
            <div className="inline-flex items-center gap-1.5 rounded-lg bg-[#F1E9E9] px-3 py-2 text-xs font-medium text-[#8A2E2C]">
              <Calendar className="w-3 h-3" /> On leave this week
            </div>
          )
        ) : (ts?.reason === "clinic_closed_this_day" || ts?.reason === "clinic_holiday") ? (
          hasNextSlot ? (
            <div className="inline-flex items-center gap-1.5 rounded-lg bg-[#FDF0E5] px-3 py-2 text-xs font-medium text-[#B0521F]">
              <Clock className="w-3 h-3" />
              Closed today · Next {new Date(nextSlot.iso).toLocaleDateString("en-IN",
                { weekday: "short", ...(tz ? { timeZone: tz } : {}) })} {fmtTime(nextSlot.iso, tz)}
            </div>
          ) : (
            <div className="inline-flex items-center gap-1.5 rounded-lg bg-[#F1E9E9] px-3 py-2 text-xs font-medium text-[#8A2E2C]">
              <Clock className="w-3 h-3" /> Clinic closed today
            </div>
          )
        ) : (ts?.reason === "doctor_not_bookable" || ts?.reason === "online_bookings_disabled") ? (
          <div className="inline-flex items-center gap-1.5 rounded-lg bg-[#F1E9E9] px-3 py-2 text-xs font-medium text-[#8A2E2C]">
            <AlertTriangle className="w-3 h-3" /> Not available online
          </div>
        ) : ts?.reason === "doctor_unavailable" ? (
          hasNextSlot ? (
            <div className="inline-flex items-center gap-1.5 rounded-lg bg-[#FDF0E5] px-3 py-2 text-xs font-medium text-[#B0521F]">
              <Clock className="w-3 h-3" /> Unavailable today
            </div>
          ) : (
            <div className="inline-flex items-center gap-1.5 rounded-lg bg-[#F1E9E9] px-3 py-2 text-xs font-medium text-[#8A2E2C]">
              <AlertTriangle className="w-3 h-3" /> Currently unavailable
            </div>
          )
        ) : ts && !ts.closed && !ts.hasSlots ? (
          hasNextSlot ? (
            <div className="inline-flex items-center gap-1.5 rounded-lg bg-[#FDF0E5] px-3 py-2 text-xs font-medium text-[#B0521F]">
              <Clock className="w-3 h-3" /> Fully booked today · Next {new Date(nextSlot.iso).toLocaleDateString("en-IN",
                { weekday: "short", ...(tz ? { timeZone: tz } : {}) })} {fmtTime(nextSlot.iso, tz)}
            </div>
          ) : (
            <div className="inline-flex items-center gap-1.5 rounded-lg bg-[#F1E9E9] px-3 py-2 text-xs font-medium text-[#8A2E2C]">
              <AlertTriangle className="w-3 h-3" /> Fully booked
            </div>
          )
        ) : hasNextSlot ? (
          <div className="inline-flex items-center gap-1.5 rounded-lg bg-[#FDF0E5] px-3 py-2 text-xs font-medium text-[#B0521F]">
            <Clock className="w-3 h-3" />
            Next available {fmtTime(nextSlot.iso, tz)}
            <span className="text-[10px] text-[#B0521F]/70">
              · {new Date(nextSlot.iso).toLocaleDateString("en-IN",
                  { weekday: "short", day: "numeric", month: "short",
                    ...(tz ? { timeZone: tz } : {}) })}
            </span>
          </div>
        ) : (
          <div className="inline-flex items-center gap-1.5 rounded-lg bg-[#F1E9E9] px-3 py-2 text-xs text-[#8A2E2C]">
            <AlertTriangle className="w-3 h-3" />
            No open slots this week
          </div>
        )}
      </div>

      {/* Consultation fee (only when the doctor + clinic expose it) */}
      {feeLabel && (
        <div
          className="rounded-lg bg-[#F5F0E3] px-3 py-2.5 flex items-center justify-between mb-4"
          data-testid={`doctor-fee-${d.id}`}
        >
          <div className="flex items-center gap-1.5 text-xs text-ink">
            <span className="w-4 h-4 rounded-full bg-white grid place-items-center text-[10px] font-semibold text-ink-mid">
              ₹
            </span>
            Consultation Fee
          </div>
          <div className="text-sm font-semibold text-ink">{feeLabel}</div>
        </div>
      )}

      {/* CTA button — visually the primary "Select" affordance */}
      <div className="mt-auto pt-1">
        <div
          className={[
            "w-full inline-flex items-center justify-center gap-2 py-2.5 rounded-xl text-sm font-medium transition-colors",
            selected
              ? "bg-[#D06A42] text-white"
              : "border border-[#D06A42]/60 text-[#D06A42] hover:bg-[#FDF0E5]",
          ].join(" ")}
          aria-hidden
        >
          {selected ? (
            <>
              <CheckCircle2 className="w-4 h-4 animate-[popIn_.25s_ease-out]" />
              Selected
            </>
          ) : (
            "Select"
          )}
        </div>
      </div>
    </button>
  );
}

/**
 * "First Available" call-to-action card. Auto-selects whichever doctor has
 * the earliest next slot. Rendered as the last card so the layout still
 * feels grid-aligned; on mobile it becomes the last carousel item.
 */
function FirstAvailableCard({ targetDoctor, nextSlot, selected, onSelect, tz }) {
  const disabled = !targetDoctor;
  return (
    <button
      type="button"
      onClick={onSelect}
      disabled={disabled}
      data-testid="doctor-first-available"
      data-selected={selected ? "true" : "false"}
      className={[
        "relative snap-start shrink-0 w-[85vw] max-w-[340px] sm:w-auto sm:max-w-none",
        "rounded-2xl border p-5 flex flex-col items-center text-center justify-center transition-all duration-200 ease-out",
        "bg-white min-h-[300px]",
        disabled ? "opacity-60 cursor-not-allowed border-[#EFEADC]" :
        selected
          ? "border-[#D06A42] shadow-[0_20px_50px_-20px_rgba(208,106,66,0.55)] sm:scale-[1.015] ring-1 ring-[#D06A42]/20"
          : "border-[#EFEADC] hover:border-[#D06A42]/40 hover:shadow-[0_12px_36px_-24px_rgba(0,0,0,0.25)]",
      ].join(" ")}
    >
      <div className="w-16 h-16 rounded-full bg-[#FDF0E5] grid place-items-center mb-4">
        <Users className="w-7 h-7 text-[#D06A42]" strokeWidth={1.6} />
      </div>
      <div className="font-display text-lg text-ink">First Available</div>
      <p className="text-xs text-ink-mid mt-2 max-w-[220px]">
        {disabled
          ? "No open slots this week — please call the clinic."
          : "Book the earliest slot with any available doctor."}
      </p>
      {!disabled && nextSlot?.iso && (
        <div className="mt-3 inline-flex items-center gap-1.5 text-[11px] font-medium text-[#B0521F]">
          <Clock className="w-3 h-3" /> {fmtTime(nextSlot.iso, tz)}
          <span className="text-[10px] text-[#B0521F]/70">
            · {targetDoctor?.name?.split(" ").slice(0, 2).join(" ")}
          </span>
        </div>
      )}
      <div className="mt-auto pt-4 w-full">
        <div
          className={[
            "w-full inline-flex items-center justify-center gap-2 py-2.5 rounded-xl text-sm font-medium transition-colors",
            selected
              ? "bg-[#D06A42] text-white"
              : "border border-[#D06A42]/60 text-[#D06A42] hover:bg-[#FDF0E5]",
          ].join(" ")}
          aria-hidden
        >
          {selected ? (
            <>
              <CheckCircle2 className="w-4 h-4" />
              Selected
            </>
          ) : (
            "Select"
          )}
        </div>
      </div>
    </button>
  );
}

/* ---------- avatar helpers reused across cards ---------- */
function _initials(name = "") {
  const parts = (name || "").trim().split(/\s+/).filter(Boolean);
  if (!parts.length) return "?";
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}
function _avatarTint(seed = "") {
  const palette = [
    "bg-[#F5E0D2] text-[#B0521F]",
    "bg-[#E8F0EB] text-[#2D6A4F]",
    "bg-[#E4E7F5] text-[#3C4B99]",
    "bg-[#F5E4EE] text-[#8E2A5A]",
    "bg-[#EFEAD8] text-[#7A5A11]",
    "bg-[#DDEBF4] text-[#1E5F84]",
  ];
  let h = 0;
  for (let i = 0; i < seed.length; i += 1) h = (h * 31 + seed.charCodeAt(i)) >>> 0;
  return palette[h % palette.length];
}


/* -------------------------- SLOT PICKER -------------------------- */
/* ==================== PREMIUM SLOT PICKER ====================
   Redesign spec (Feb 2026):
   - Horizontal scrollable date strip (Today · Tomorrow · Tue · Wed · …)
     with premium rounded cards. Only ONE date is active at a time.
   - Slots for the active date are grouped into Morning · Afternoon ·
     Evening accordions. Only ONE accordion is expanded at once.
   - Initial chips per group is capped at 6; "+N more" reveals the rest
     with a smooth height animation.
   - Chip states: idle (soft pill) · hover (lift + shadow) · selected
     (orange gradient + check icon + scale) · disabled (muted, no hover).
   - Keyboard: Tab moves focus; Enter/Space selects; Escape collapses.
   - Backend contract is UNCHANGED — same /available-slots endpoint,
     same date lookahead, same doctor scoping.
   ============================================================= */

// Daypart boundaries. Morning ≤ 11, Afternoon ≤ 16, Evening ≥ 17.
const DAYPARTS = [
  { key: "morning",   label: "Morning",   glyph: "☀",  hourMin: 0,  hourMax: 11 },
  { key: "afternoon", label: "Afternoon", glyph: "🌤", hourMin: 12, hourMax: 16 },
  { key: "evening",   label: "Evening",   glyph: "🌙", hourMin: 17, hourMax: 23 },
];
const INITIAL_CHIPS_PER_PART = 6;

// Extract clinic-tz HH from an ISO string. We parse against the clinic tz
// so the daypart grouping matches how the CLINIC sees the day, not the
// visitor's browser tz.
function _hourInTz(iso, tz) {
  try {
    const parts = new Intl.DateTimeFormat("en-GB", {
      hour: "2-digit", hour12: false, timeZone: tz || undefined,
    }).formatToParts(new Date(iso));
    const hh = parts.find((p) => p.type === "hour")?.value ?? "0";
    return parseInt(hh, 10);
  } catch { return new Date(iso).getHours(); }
}

function _groupByDaypart(slots, tz) {
  const buckets = { morning: [], afternoon: [], evening: [] };
  for (const s of slots || []) {
    // Prefer the server-classified daypart — the backend computes it in
    // the CLINIC'S timezone, so it's immune to browser/pod tz drift that
    // used to make 17:00 IST appear under Morning on UTC servers.
    if (s.daypart && buckets[s.daypart]) {
      buckets[s.daypart].push(s);
      continue;
    }
    // Legacy fallback for older backends that don't return `daypart`.
    const h = _hourInTz(s.start_iso, tz);
    if (h <= 11) buckets.morning.push(s);
    else if (h <= 16) buckets.afternoon.push(s);
    else buckets.evening.push(s);
  }
  return buckets;
}

function _shortDay(iso, tz) {
  // "Mon" / "Tue" / …
  return new Intl.DateTimeFormat("en-GB",
    { weekday: "short", ...(tz ? { timeZone: tz } : {}) })
      .format(new Date(iso + "T09:00:00")).slice(0, 3);
}
function _shortDate(iso, tz) {
  // "3 Aug"
  return new Intl.DateTimeFormat("en-GB",
    { day: "numeric", month: "short", ...(tz ? { timeZone: tz } : {}) })
      .format(new Date(iso + "T09:00:00"));
}

function SlotPicker({ slug, clinic, selected, onPick, selectedDoctorId }) {
  const [byDay, setByDay] = useState({});
  const [loading, setLoading] = useState(true);
  const [activeDate, setActiveDate] = useState(null);
  const [expandedPart, setExpandedPart] = useState("morning");
  const [showAllPart, setShowAllPart] = useState({});   // { morning: true, ... }

  const advanceDays = clinic.booking_config?.advance_days ?? 30;
  const tz = clinic?.timezone;

  // Next-N-days lookahead (min 7, max 14 shown — beyond that the strip
  // just scrolls; the /available-slots endpoint still respects the
  // configured max lookahead).
  const days = useMemo(() => {
    const arr = [];
    const N = Math.min(14, advanceDays);
    for (let i = 0; i < N; i++) {
      const d = new Date(); d.setDate(d.getDate() + i);
      arr.push(dayISO(d));
    }
    return arr;
  }, [advanceDays]);

  const load = useCallback(async () => {
    setLoading(true);
    setByDay({});
    const results = await Promise.all(days.map((d) => {
      const params = { date: d };
      if (selectedDoctorId) params.doctor_id = selectedDoctorId;
      return api.get(`/public/clinics/${slug}/available-slots`, { params })
         .then((r) => [d, r.data])
         .catch(() => [d, { closed: true, slots: [] }]);
    }));
    setByDay(Object.fromEntries(results));
    setLoading(false);
  }, [slug, days, selectedDoctorId]);

  useEffect(() => {
    load();
    const t = setInterval(load, 25000);
    return () => clearInterval(t);
  }, [load]);

  // Auto-select the earliest date that actually has slots.
  useEffect(() => {
    if (loading) return;
    if (activeDate && (byDay[activeDate]?.slots?.length || 0) > 0) return;
    const firstOpen = days.find((d) => (byDay[d]?.slots?.length || 0) > 0);
    setActiveDate(firstOpen || days[0]);
  }, [loading, byDay, days, activeDate]);

  // Auto-expand the daypart of the earliest slot on the active date.
  useEffect(() => {
    if (!activeDate) return;
    const grouped = _groupByDaypart(byDay[activeDate]?.slots || [], tz);
    const firstFilled = DAYPARTS.find((p) => grouped[p.key].length > 0);
    setExpandedPart(firstFilled?.key || null);
    setShowAllPart({});
  }, [activeDate, byDay, tz]);

  const nothingAvailable = !loading && days.every(
    (d) => (byDay[d]?.slots?.length || 0) === 0);

  const emptyMessage = useMemo(() => {
    if (!nothingAvailable) return null;
    const reasons = days.map((d) => byDay[d]?.reason).filter(Boolean);
    const uniq = [...new Set(reasons)];
    if (uniq.length === 1) {
      const r = uniq[0];
      if (r === "doctor_on_leave") return {
        title: "Doctor on leave", body: "The doctor you selected is on leave for the week. Try another doctor or call the clinic.",
      };
      if (r === "clinic_closed_this_day" || r === "clinic_holiday") return {
        title: "Clinic Closed", body: "The clinic is closed this week. Please try again later or call directly.",
      };
      if (r === "doctor_not_bookable" || r === "online_bookings_disabled") return {
        title: "Not accepting online bookings", body: "Please call the clinic directly to book with this doctor.",
      };
      if (r === "doctor_unavailable") return {
        title: "Doctor unavailable", body: "This doctor isn't accepting new bookings right now.",
      };
    }
    const allOpenButFull = days.every((d) => {
      const row = byDay[d];
      return row && row.closed === false && (row.slots || []).length === 0;
    });
    if (allOpenButFull) return {
      title: "Fully booked this week",
      body: "Every slot in the next 7 days is taken. Try again later or call the clinic.",
    };
    return {
      title: "No slots available in the next week",
      body: "Please call the clinic to check.",
    };
  }, [nothingAvailable, days, byDay]);

  const activeRow = activeDate ? byDay[activeDate] : null;
  const activeSlotCount = activeRow?.slots?.length || 0;
  const grouped = useMemo(
    () => _groupByDaypart(activeRow?.slots || [], tz),
    [activeRow, tz]);

  return (
    <section
      className="rounded-3xl bg-white/70 backdrop-blur-md border border-[#E7E1D3] p-5 sm:p-8 shadow-sm"
      data-testid="slot-picker"
    >
      <div className="flex items-center justify-between gap-3 mb-5">
        <div className="flex items-center gap-2">
          <Calendar className="w-4 h-4 text-ink" />
          <div className="text-[11px] uppercase tracking-[0.22em] text-ink-mid">
            Choose a date &amp; time
          </div>
        </div>
        <div className="flex items-center gap-2">
          {loading && <Loader2 className="w-3.5 h-3.5 animate-spin text-ink-soft" />}
          {!loading && activeSlotCount > 0 && (
            <span className="text-[11px] font-medium text-emerald-700 bg-emerald-50 border border-emerald-100 rounded-full px-2.5 py-1"
                  data-testid="slots-available-count">
              {activeSlotCount} slot{activeSlotCount === 1 ? "" : "s"} available
            </span>
          )}
          {!loading && activeDate && activeSlotCount === 0 && !nothingAvailable && (
            <span className="text-[11px] font-medium text-rose-700 bg-rose-50 border border-rose-100 rounded-full px-2.5 py-1">
              Fully booked
            </span>
          )}
        </div>
      </div>

      {loading && (
        <div className="py-16 text-center text-sm text-ink-soft" data-testid="slots-loading">
          <Loader2 className="w-6 h-6 animate-spin inline-block" />
        </div>
      )}

      {nothingAvailable && emptyMessage && (
        <div className="py-14 text-center" data-testid="slots-empty"
             data-empty-reason={days.map((d) => byDay[d]?.reason).find(Boolean) || "none"}>
          <AlertTriangle className="w-6 h-6 text-ink-soft mx-auto mb-2" />
          <div className="text-sm text-ink font-medium">{emptyMessage.title}</div>
          <div className="text-xs text-ink-mid mt-1">{emptyMessage.body}</div>
        </div>
      )}

      {!loading && !nothingAvailable && (
        <>
          <DateStrip
            days={days}
            byDay={byDay}
            activeDate={activeDate}
            onSelectDate={setActiveDate}
            tz={tz}
          />

          <div className="mt-6 space-y-3" role="region" aria-label="Available time slots">
            {DAYPARTS.map((part) => (
              <DaypartAccordion
                key={part.key}
                part={part}
                slots={grouped[part.key]}
                expanded={expandedPart === part.key}
                showAll={!!showAllPart[part.key]}
                onToggleExpand={() =>
                  setExpandedPart(expandedPart === part.key ? null : part.key)}
                onToggleShowAll={() =>
                  setShowAllPart((s) => ({ ...s, [part.key]: !s[part.key] }))}
                selected={selected}
                onPick={onPick}
                tz={tz}
              />
            ))}
            {activeSlotCount === 0 && (
              <div className="rounded-2xl border border-dashed border-[#E7E1D3] bg-[#FBF7EE] py-8 px-6 text-center">
                <div className="text-sm font-medium text-ink">No slots this day</div>
                <div className="text-xs text-ink-mid mt-1">Pick another date from the strip above.</div>
              </div>
            )}
          </div>
        </>
      )}
    </section>
  );
}

function DateStrip({ days, byDay, activeDate, onSelectDate, tz }) {
  const today = dayISO(new Date());
  return (
    <div
      className="-mx-1 flex gap-2 overflow-x-auto snap-x snap-mandatory pb-1 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
      role="tablist"
      aria-label="Available dates"
      data-testid="date-strip"
    >
      {days.map((d, i) => {
        const row = byDay[d];
        const count = row?.slots?.length || 0;
        const isActive = d === activeDate;
        const isToday = d === today;
        const isPastOrClosed = row && row.closed;
        const label = isToday ? "Today"
                    : (i === 1 ? "Tomorrow" : _shortDay(d, tz));
        return (
          <button
            key={d}
            role="tab"
            aria-selected={isActive}
            aria-label={`${label} ${_shortDate(d, tz)} — ${count} slots available`}
            tabIndex={isActive ? 0 : -1}
            onClick={() => onSelectDate(d)}
            data-testid={`date-card-${d}`}
            data-active={isActive ? "true" : "false"}
            className={[
              "snap-start shrink-0 min-w-[92px] rounded-2xl px-4 py-3 text-left",
              "transition-all duration-200 ease-out will-change-transform",
              "border focus:outline-none focus-visible:ring-2 focus-visible:ring-[#D06A42] focus-visible:ring-offset-2",
              isActive
                ? "bg-gradient-to-br from-[#D06A42] to-[#E17550] text-white border-transparent shadow-lg shadow-[#D06A42]/25 scale-[1.02]"
                : count === 0
                  ? "bg-white/60 border-[#EFEADC] text-ink-soft hover:border-[#E7E1D3]"
                  : "bg-white border-[#E7E1D3] text-ink hover:border-[#D06A42] hover:-translate-y-0.5 hover:shadow-md",
            ].join(" ")}
          >
            <div className={`text-[10px] uppercase tracking-widest ${isActive ? "text-white/85" : "text-ink-soft"}`}>
              {label}
            </div>
            <div className={`mt-1 font-display text-lg leading-none ${isActive ? "text-white" : "text-ink"}`}>
              {_shortDate(d, tz)}
            </div>
            <div className={`mt-1.5 text-[10px] font-medium ${
              isActive ? "text-white/90"
              : count > 0 ? "text-emerald-700"
              : isPastOrClosed ? "text-rose-600" : "text-ink-soft"}`}>
              {count > 0 ? `${count} slot${count === 1 ? "" : "s"}`
                : isPastOrClosed ? "Closed" : "Full"}
            </div>
            {isToday && !isActive && (
              <div className="mt-1 inline-block text-[9px] uppercase tracking-widest text-[#D06A42] font-semibold">
                • Now
              </div>
            )}
          </button>
        );
      })}
    </div>
  );
}

function DaypartAccordion({
  part, slots, expanded, showAll, onToggleExpand, onToggleShowAll,
  selected, onPick, tz,
}) {
  const count = slots.length;
  const hasSlots = count > 0;
  const visible = showAll ? slots : slots.slice(0, INITIAL_CHIPS_PER_PART);
  const hidden = Math.max(0, count - INITIAL_CHIPS_PER_PART);

  return (
    <div
      data-testid={`daypart-${part.key}`}
      className={[
        "rounded-2xl border transition-all duration-200",
        hasSlots
          ? expanded
            ? "border-[#E7E1D3] bg-white shadow-sm"
            : "border-[#EFEADC] bg-white/60 hover:border-[#E7E1D3]"
          : "border-[#EFEADC] bg-[#FBF7EE]/40 opacity-60",
      ].join(" ")}
    >
      <button
        type="button"
        onClick={hasSlots ? onToggleExpand : undefined}
        disabled={!hasSlots}
        aria-expanded={expanded}
        aria-controls={`daypart-panel-${part.key}`}
        data-testid={`daypart-toggle-${part.key}`}
        className="w-full flex items-center justify-between gap-3 px-5 py-3.5 text-left rounded-2xl focus:outline-none focus-visible:ring-2 focus-visible:ring-[#D06A42] focus-visible:ring-offset-2 disabled:cursor-not-allowed"
      >
        <div className="flex items-center gap-3">
          <span className="text-lg leading-none" aria-hidden>{part.glyph}</span>
          <div>
            <div className="font-display text-[15px] text-ink leading-tight">{part.label}</div>
            <div className="text-[11px] text-ink-soft mt-0.5">
              {hasSlots ? `${count} slot${count === 1 ? "" : "s"} available` : "No slots"}
            </div>
          </div>
        </div>
        {hasSlots && (
          <ChevronRight
            className={`w-4 h-4 text-ink-mid transition-transform duration-200 ${expanded ? "rotate-90" : ""}`}
            aria-hidden
          />
        )}
      </button>

      {/* Animated height container. We use grid-template-rows trick: 1fr when
          expanded, 0fr when collapsed, letting CSS smoothly interpolate. */}
      <div
        id={`daypart-panel-${part.key}`}
        className={`grid transition-[grid-template-rows] duration-300 ease-in-out ${
          expanded && hasSlots ? "grid-rows-[1fr]" : "grid-rows-[0fr]"
        }`}
        aria-hidden={!expanded}
      >
        <div className="overflow-hidden">
          <div className="px-5 pb-5 pt-1">
            {/* Chip grid — 2-col mobile, 4-col tablet, 5-col desktop */}
            <div
              className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-2.5"
              role="listbox"
              aria-label={`${part.label} slots`}
            >
              {visible.map((s) => (
                <SlotChip
                  key={s.start_iso}
                  slot={s}
                  selected={selected === s.start_iso}
                  onPick={onPick}
                  tz={tz}
                />
              ))}
            </div>
            {hidden > 0 && (
              <div className="mt-3 flex justify-center">
                <button
                  type="button"
                  onClick={onToggleShowAll}
                  data-testid={`daypart-more-${part.key}`}
                  className="inline-flex items-center gap-1.5 text-xs font-medium text-ink-mid hover:text-[#D06A42] px-3 py-1.5 rounded-full transition-colors"
                >
                  {showAll ? "Collapse" : `+${hidden} more ${part.label.toLowerCase()} slot${hidden === 1 ? "" : "s"}`}
                  <ChevronRight className={`w-3.5 h-3.5 transition-transform ${showAll ? "-rotate-90" : "rotate-90"}`} />
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function SlotChip({ slot, selected, onPick, tz }) {
  const isDisabled = slot.capacity_left != null && slot.capacity_left <= 0;
  // Prefer the server-computed local time label so the chip always shows
  // the CLINIC's local time, immune to browser/pod tz drift. Falls back
  // to fmtTime24 for older backends that don't return `hm_local`.
  const label = slot.hm_local || fmtTime(slot.start_iso, tz);
  return (
    <button
      type="button"
      role="option"
      aria-selected={selected}
      aria-label={`Book ${label}`}
      disabled={isDisabled}
      onClick={() => onPick(slot.start_iso)}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onPick(slot.start_iso); }
      }}
      data-testid={`slot-${slot.start_iso}`}
      data-selected={selected ? "true" : "false"}
      className={[
        "group relative inline-flex items-center justify-center gap-1.5",
        "h-11 px-3 rounded-full text-[13px] font-medium",
        "transition-all duration-200 ease-out will-change-transform",
        "focus:outline-none focus-visible:ring-2 focus-visible:ring-[#D06A42] focus-visible:ring-offset-2",
        "active:scale-[0.97]",
        isDisabled
          ? "bg-white/50 border border-[#EFEADC] text-ink-soft cursor-not-allowed"
          : selected
            ? "bg-gradient-to-br from-[#D06A42] to-[#E17550] text-white border border-transparent shadow-lg shadow-[#D06A42]/25 scale-[1.03]"
            : "bg-white border border-[#E8EAE9] text-ink hover:border-[#D06A42] hover:-translate-y-0.5 hover:shadow-md",
      ].join(" ")}
    >
      {selected && (
        <CheckCircle2 className="w-3.5 h-3.5 -ml-0.5 animate-in fade-in zoom-in duration-200" aria-hidden />
      )}
      <span className="tabular-nums tracking-tight">{label}</span>
    </button>
  );
}

/* -------------------------- BOOKING FORM -------------------------- */
function BookingForm({ slug, clinic, procedures, selectedISO, onBack, onBooked,
                      hiddenIds, hiddenLabel, isAdminPreview, doctors,
                      selectedDoctorId, onDoctorChange }) {
  const [form, setForm] = useState({
    name: "", phone: "", email: "", procedure: procedures?.[0]?.name || "Consultation",
    doctor_id: selectedDoctorId || "", duration_min: 30, notes: "", hp: "",
  });
  // Keep the form's doctor_id locked to the page-level selection.  Users can
  // still switch doctor inside the form (via the dropdown) — that call flows
  // back through `onDoctorChange` so the SlotPicker's next mount also updates.
  useEffect(() => {
    setForm((f) => (f.doctor_id === (selectedDoctorId || "") ? f
                                                              : { ...f, doctor_id: selectedDoctorId || "" }));
  }, [selectedDoctorId]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  // OTP state
  const [otpSent, setOtpSent] = useState(false);
  const [otpVerified, setOtpVerified] = useState(false);
  const [otpCode, setOtpCode] = useState("");
  const [otpDebug, setOtpDebug] = useState(null); // dev fallback
  const [otpBusy, setOtpBusy] = useState(false);
  const [otpStatus, setOtpStatus] = useState(null);   // "sending" | "delivered" | "failed" | "expired" | "verified"
  const [otpReason, setOtpReason] = useState(null);   // human-readable text from backend
  const [resendCooldown, setResendCooldown] = useState(0);   // seconds

  // Countdown ticker for the resend button.
  useEffect(() => {
    if (resendCooldown <= 0) return;
    const t = setTimeout(() => setResendCooldown((s) => Math.max(0, s - 1)), 1000);
    return () => clearTimeout(t);
  }, [resendCooldown]);

  const sendOtp = async () => {
    if (!form.phone) return setError("Enter your mobile number first");
    setError(null); setOtpBusy(true); setOtpStatus("sending"); setOtpReason(null);
    try {
      const { data } = await api.post("/public/booking/otp/send",
        { slug, phone: form.phone });
      setOtpSent(true);
      setOtpDebug(data.debug_code || null);
      setOtpStatus(data.delivered ? "delivered" : "sent");
      setOtpReason(data.reason || (data.delivered ? "Delivered to your WhatsApp." : null));
      setResendCooldown(30);  // 30-second cooldown before Resend is enabled
    } catch (e) {
      setOtpStatus("failed");
      const msg = apiError(e, "Could not send code");
      setOtpReason(msg);
      setError(msg);
    } finally { setOtpBusy(false); }
  };

  const verifyOtp = async () => {
    if (!otpCode.trim()) return;
    setError(null); setOtpBusy(true);
    try {
      await api.post("/public/booking/otp/verify",
        { slug, phone: form.phone, code: otpCode.trim() });
      setOtpVerified(true);
      setOtpStatus("verified");
    } catch (e) {
      const msg = apiError(e, "Wrong code");
      // "expired" errors from backend carry the word "expired".
      if (/expired/i.test(msg)) setOtpStatus("expired");
      setError(msg);
    } finally { setOtpBusy(false); }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (busy) return;
    if (!otpVerified) return setError("Please verify your mobile number first");
    setBusy(true); setError(null);
    try {
      // ── Reschedule mode ────────────────────────────────────────────
      // When the page was reached via the WhatsApp Reschedule button, the
      // URL carries a signed `?rid=<jwt>` token. We MUST call the atomic
      // move endpoint so the ORIGINAL appointment gets updated in-place
      // (no duplicate row, chair preserved). See P0 fix Mar 2026.
      const rescheduleToken = new URLSearchParams(window.location.search).get("rid");
      if (rescheduleToken) {
        const { data } = await api.post(
          `/public/clinics/${slug}/reschedule`,
          { rid: rescheduleToken, start: selectedISO, hp: form.hp }
        );
        onBooked({
          ...data,
          when: selectedISO,
          confirmation_pdf_url: `/api/public/booking/${data.appointment_id}/confirmation.pdf`,
          rescheduled: true,
        });
        return;
      }

      const { data } = await api.post(`/public/clinics/${slug}/book`,
        { ...form, start: selectedISO });
      onBooked(data);
    } catch (err) {
      setError(apiError(err, "Could not book — please try another slot."));
    } finally {
      setBusy(false);
    }
  };

  const proc = procedures.find((p) => p.name === form.procedure);
  const procCost = proc?.cost;
  const procFeeHidden = !!proc && (
    (proc.price_visible === false) ||
    (hiddenIds && hiddenIds.has(proc.id)) ||
    proc.cost == null
  );

  // Consultation fee lives on the DOCTOR (per-doctor). Fall back to the first
  // active doctor when the patient hasn't picked one, so the fee section still
  // renders sensibly.
  const activeDoctor = (doctors || []).find((d) => d.id === form.doctor_id) || (doctors || [])[0] || null;
  const doctorConsultFee = activeDoctor?.consultation_fee;
  const doctorConsultHidden = !!activeDoctor && (
    activeDoctor.consultation_fee_hidden === true ||
    activeDoctor.price_visible === false
  );
  // "Free consultation" = doctor visible AND fee explicitly null/0.
  const consultIsFree = activeDoctor && !doctorConsultHidden && (doctorConsultFee == null || Number(doctorConsultFee) === 0);
  const showPricingMaster = clinic?.booking_config?.show_pricing !== false;

  return (
    <section className="rounded-3xl bg-white/85 backdrop-blur border border-[#E7E1D3] p-5 sm:p-8 shadow-sm"
             data-testid="booking-form">
      <button onClick={onBack} className="inline-flex items-center gap-1 text-xs text-ink-mid hover:text-ink mb-4"
              data-testid="btn-back-to-slots">
        <ArrowLeft className="w-3.5 h-3.5" /> Back to slots
      </button>

      <div className="rounded-2xl bg-[#F5F0E3] border border-[#EFEADC] p-4 mb-6">
        <div className="text-[10px] uppercase tracking-widest text-ink-soft">You've picked</div>
        <div className="mt-1 font-display text-lg text-ink">{fmtLongDate(selectedISO, clinic?.timezone)}</div>
        <div className="text-sm text-ink-mid">at {fmtTime(selectedISO, clinic?.timezone)}</div>
      </div>

      <form onSubmit={submit} className="space-y-3">
        {/* Honeypot: hidden bot trap */}
        <input type="text" name="hp" value={form.hp}
               onChange={(e) => setForm({ ...form, hp: e.target.value })}
               tabIndex={-1} autoComplete="off"
               style={{ position: "absolute", left: "-9999px", opacity: 0 }} />

        <Field label="Your name" required>
          <input required minLength={2} value={form.name} data-testid="form-name"
                 onChange={(e) => setForm({ ...form, name: e.target.value })}
                 className="input" placeholder="Full name" />
        </Field>
        <Field label="Mobile number" required>
          <div className="flex gap-2">
            <input required value={form.phone} data-testid="form-phone" inputMode="tel"
                   disabled={otpVerified}
                   onChange={(e) => { setForm({ ...form, phone: e.target.value }); setOtpSent(false); setOtpVerified(false); }}
                   className="input flex-1" placeholder="+91 9810453892" />
            {!otpVerified && (
              <button type="button" onClick={sendOtp} disabled={otpBusy || !form.phone || (otpSent && resendCooldown > 0)}
                      data-testid="btn-send-otp"
                      className="px-3 py-2 rounded-xl bg-[#F0EBDD] hover:bg-[#E7DEC5] text-ink text-xs font-medium whitespace-nowrap disabled:opacity-50">
                {otpBusy ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
                 : otpSent ? (resendCooldown > 0 ? `Resend in ${resendCooldown}s` : "Resend")
                           : "Send code"}
              </button>
            )}
            {otpVerified && (
              <span className="inline-flex items-center gap-1 px-3 py-2 rounded-xl bg-[#E8F0EB] text-[#2D6A4F] text-xs font-medium" data-testid="otp-verified">
                <CheckCircle2 className="w-3.5 h-3.5" /> Verified
              </span>
            )}
          </div>
          {/* Status chip — sent/delivered/failed/expired/verified */}
          {otpStatus && !otpVerified && (
            <div className="mt-2 flex items-start gap-1.5" data-testid={`otp-status-${otpStatus}`}>
              <span
                className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] uppercase tracking-widest font-medium ${
                  otpStatus === "delivered" ? "bg-[#E8F0EB] text-[#2D6A4F]" :
                  otpStatus === "sending"   ? "bg-linen text-ink-mid" :
                  otpStatus === "sent"      ? "bg-[#FDF3ED] text-[#8A3F1E]" :
                  otpStatus === "expired"   ? "bg-[#F5E7E4] text-[#8A2D2D]" :
                                              "bg-[#F5E7E4] text-[#8A2D2D]"
                }`}
              >
                {otpStatus === "sending" ? "Sending…" : otpStatus}
              </span>
              {otpReason && (
                <span className="text-[10px] text-ink-mid leading-snug">{otpReason}</span>
              )}
            </div>
          )}
          {otpSent && !otpVerified && (
            <div className="mt-2 flex gap-2">
              <input value={otpCode} onChange={(e) => setOtpCode(e.target.value)}
                     data-testid="form-otp" inputMode="numeric" maxLength={6}
                     className="input flex-1 tracking-widest text-center font-mono"
                     placeholder="6-digit code" />
              <button type="button" onClick={verifyOtp} disabled={otpBusy || otpCode.length !== 6}
                      data-testid="btn-verify-otp"
                      className="px-4 py-2 rounded-xl bg-pine text-linen text-xs font-medium disabled:opacity-50">
                Verify
              </button>
            </div>
          )}
          {otpDebug && !otpVerified && (
            <div className="mt-1 text-[10px] text-ink-soft">
              (Dev mode) code: <span className="font-mono">{otpDebug}</span>
            </div>
          )}
        </Field>
        <Field label="Email (optional)">
          <input type="email" value={form.email} data-testid="form-email"
                 onChange={(e) => setForm({ ...form, email: e.target.value })}
                 className="input" placeholder="you@example.com" />
        </Field>
        <Field label="Reason for visit">
          <select value={form.procedure} data-testid="form-procedure"
                  onChange={(e) => setForm({ ...form, procedure: e.target.value })}
                  className="input">
            {procedures.map((p, i) => (
              <option key={`${p.name}-${p.variant || i}`} value={p.name}>
                {p.name}{p.variant ? ` · ${p.variant}` : ""}
              </option>
            ))}
          </select>
        </Field>
        <Field label="Notes (optional)">
          <textarea value={form.notes} rows={2} data-testid="form-notes"
                    onChange={(e) => setForm({ ...form, notes: e.target.value })}
                    className="input" placeholder="Anything the doctor should know?" />
        </Field>

        {showPricingMaster && (
          <div className="rounded-xl bg-[#FBFAF6] border border-[#EFEADC] p-3 space-y-2"
               data-testid="fee-row">
            <div className="flex items-start justify-between gap-3">
              <div>
                <div className="text-[10px] uppercase tracking-widest text-ink-soft">Reason for Visit</div>
                <div className="text-sm text-ink font-medium mt-0.5" data-testid="reason-for-visit">
                  {proc ? `${proc.name}${proc.variant ? ` · ${proc.variant}` : ""} Consultation` : "Consultation"}
                </div>
              </div>
              <div className="text-right">
                <div className="text-[10px] uppercase tracking-widest text-ink-soft">Consultation Fee</div>
                {doctorConsultHidden && !isAdminPreview ? (
                  <div className="text-sm text-ink-mid italic" data-testid="consult-fee-hidden">
                    Discussed at clinic
                  </div>
                ) : consultIsFree ? (
                  <div className="font-display text-lg text-ink" data-testid="consult-fee-free">
                    Free
                  </div>
                ) : (
                  <div className="font-display text-lg text-ink" data-testid="consult-fee-value">
                    ₹{Number(doctorConsultFee).toLocaleString("en-IN")}
                    {doctorConsultHidden && isAdminPreview && (
                      <span className="ml-2 align-middle text-[9px] uppercase tracking-widest px-1.5 py-0.5 rounded-full bg-[#F5E0D2] text-[#B0521F]"
                            data-testid="consult-fee-hidden-tag">
                        hidden publicly
                      </span>
                    )}
                  </div>
                )}
              </div>
            </div>
            {/* Optional line clarifying that any treatment cost is separate. */}
            {procCost != null && !procFeeHidden && (
              <div className="text-[11px] text-ink-soft border-t border-[#EFEADC] pt-2">
                Estimated treatment charges of{" "}
                <b>₹{Number(procCost).toLocaleString("en-IN")}</b>{" "}
                (if required) will be discussed after your consultation.
              </div>
            )}
            {procCost == null && !consultIsFree && (
              <div className="text-[11px] text-ink-soft border-t border-[#EFEADC] pt-2">
                Treatment charges (if required) will be discussed after your consultation.
              </div>
            )}
          </div>
        )}

        {error && (
          <div className="rounded-xl bg-[#FCECEC] border border-[#F2D2BC] px-3 py-2 text-sm text-[#B94A48]"
               data-testid="form-error">{error}</div>
        )}

        <button type="submit" disabled={busy} data-testid="btn-confirm-booking"
                className="w-full inline-flex items-center justify-center gap-2 px-5 py-3 rounded-xl bg-pine text-linen font-medium hover:bg-[#264A3B] disabled:opacity-60 transition-colors">
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />}
          Confirm booking
        </button>
        <div className="text-[11px] text-ink-soft text-center">
          By booking you agree to receive a WhatsApp confirmation from the clinic.
        </div>
      </form>

      <style>{`
        .input { width: 100%; padding: 0.65rem 0.9rem; border-radius: 0.75rem;
                 border: 1px solid #E8EAE9; background: white; font-size: 0.9rem;
                 color: #1A362B; }
        .input:focus { outline: none; border-color: #1A362B; box-shadow: 0 0 0 3px rgba(26,54,43,.12); }
      `}</style>
    </section>
  );
}

function Field({ label, required, children }) {
  return (
    <label className="block">
      <div className="text-xs text-ink-mid mb-1">
        {label} {required && <span className="text-[#D06A42]">*</span>}
      </div>
      {children}
    </label>
  );
}

/* -------------------------- CONFIRMATION -------------------------- */
function ConfirmationCard({ clinic, selectedISO, result }) {
  const gcalUrl = useMemo(() => {
    const start = new Date(selectedISO);
    const end = new Date(start.getTime() + 30 * 60000);
    const fmt = (d) => d.toISOString().replace(/[-:]|\.\d{3}/g, "");
    const params = new URLSearchParams({
      action: "TEMPLATE",
      text: `Appointment · ${clinic.name}`,
      dates: `${fmt(start)}/${fmt(end)}`,
      location: clinic.address || "",
      details: `Booking #${(result.appointment_id || "").slice(0, 8).toUpperCase()}\nCall ${clinic.phone || ""}`,
    });
    return `https://calendar.google.com/calendar/render?${params}`;
  }, [clinic, selectedISO, result]);

  const digits = (clinic?.phone || "").replace(/[^\d+]/g, "");
  const mapUrl = clinic?.google_maps_url
    || (clinic?.address ? `https://maps.google.com/?q=${encodeURIComponent(clinic.address)}` : null);

  return (
    <section className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-[#E8F0EB] to-[#F5F0E3] border border-[#B8D6C2] p-6 sm:p-10 text-center"
             data-testid="booking-confirmation">
      <div className="w-16 h-16 rounded-full bg-[#2D6A4F] text-white grid place-items-center mx-auto mb-4 shadow-lg">
        <CheckCircle2 className="w-8 h-8" />
      </div>
      <div className="text-[11px] uppercase tracking-[0.22em] text-ink-mid">You're booked</div>
      <h2 className="font-display text-3xl sm:text-4xl text-ink mt-2">We'll see you then 🌿</h2>

      <div className="mt-6 inline-block rounded-2xl bg-white/70 border border-white p-5">
        <div className="text-xs text-ink-mid">Appointment #</div>
        <div className="font-mono text-lg tracking-widest text-ink">
          {(result.appointment_id || "").slice(0, 8).toUpperCase()}
        </div>
        <div className="mt-3 text-sm text-ink">
          <div className="font-medium">{fmtLongDate(selectedISO, clinic?.timezone)}</div>
          <div className="text-ink-mid">at {fmtTime(selectedISO, clinic?.timezone)}</div>
        </div>
      </div>

      <div className="mt-6 flex flex-wrap justify-center gap-2">
        <a href={gcalUrl} target="_blank" rel="noopener noreferrer" data-testid="btn-add-to-calendar"
           className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-pine text-linen text-sm font-medium hover:bg-[#264A3B]">
          <Calendar className="w-4 h-4" /> Add to Google Calendar
        </a>
        {result.confirmation_pdf_url && (
          <a href={`${BASE}${result.confirmation_pdf_url}`} target="_blank" rel="noopener noreferrer"
             data-testid="btn-download-pdf"
             className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-white border border-pine/20 text-sm text-ink hover:bg-linen">
            <Download className="w-4 h-4" /> Download PDF
          </a>
        )}
        {mapUrl && (
          <a href={mapUrl} target="_blank" rel="noopener noreferrer"
             className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-white border border-pine/20 text-sm text-ink hover:bg-linen">
            <MapPin className="w-4 h-4" /> Directions
          </a>
        )}
        {digits && (
          <a href={`tel:${digits}`}
             className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-white border border-pine/20 text-sm text-ink hover:bg-linen">
            <Phone className="w-4 h-4" /> Call clinic
          </a>
        )}
      </div>

      <div className="mt-6 text-xs text-ink-mid">
        {result.whatsapp_status === "sent"
          ? "We've sent a WhatsApp confirmation to your phone."
          : "The clinic will send you a reminder before your visit."}
      </div>
    </section>
  );
}

/* -------------------------- TRUST FOOTER -------------------------- */
function TrustFooter({ clinic }) {
  return (
    <footer className="rounded-3xl bg-white/60 backdrop-blur border border-[#E7E1D3] p-5 grid sm:grid-cols-2 gap-4"
            data-testid="booking-footer">
      <div>
        {clinic.phone   && <div className="flex items-center gap-2 text-sm text-ink/80"><Phone className="w-3.5 h-3.5" /> {clinic.phone}</div>}
        {clinic.address && <div className="flex items-start gap-2 text-sm text-ink-mid mt-1"><MapPin className="w-3.5 h-3.5 mt-0.5" /> {clinic.address}</div>}
        {clinic.emergency_phone && (
          <div className="flex items-center gap-2 text-xs text-[#B94A48] mt-2">
            <AlertTriangle className="w-3.5 h-3.5" /> Emergency: {clinic.emergency_phone}
          </div>
        )}
      </div>
      <div className="sm:text-right space-y-1">
        <div className="inline-flex items-center gap-1 text-xs text-ink-mid">
          <ShieldCheck className="w-3.5 h-3.5" /> Secure booking
        </div>
        <div className="text-[10px] text-ink-soft">
          Powered by <span className="font-display text-ink">Viyora</span>
        </div>
      </div>
    </footer>
  );
}

function LoadingState() {
  return (
    <div className="min-h-screen bg-[#F6F5F1] grid place-items-center text-ink-mid">
      <div className="flex items-center gap-3"><Loader2 className="w-5 h-5 animate-spin" /> Loading…</div>
    </div>
  );
}

function ErrorState({ message, clinic }) {
  return (
    <div className="min-h-screen bg-[#F6F5F1] grid place-items-center p-6">
      <div className="max-w-md bg-white rounded-3xl border border-[#E7E1D3] p-8 text-center">
        <AlertTriangle className="w-10 h-10 text-clay mx-auto mb-3" />
        <h1 className="font-display text-2xl text-ink mb-2">Not available</h1>
        <p className="text-sm text-ink-mid">{message}</p>
        {clinic?.phone && (
          <a href={`tel:${clinic.phone}`}
             className="mt-4 inline-flex items-center gap-2 px-4 py-2 rounded-full bg-pine text-linen text-sm">
            <Phone className="w-4 h-4" /> Call {clinic.phone}
          </a>
        )}
      </div>
    </div>
  );
}
